import { confirmable } from 'react-confirm';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { Icon, InlineStack, TextField, Tooltip } from '@shopify/polaris';
import { BarcodeIcon, CheckCircleIcon, InfoIcon, XCircleIcon } from '@shopify/polaris-icons';
import { useScanSound } from 'src/hooks/use_scan_sound';
import i18n from 'src/locales/i18n';

i18n.loadNamespaces(['sorting']);

function ToteScanDialog({
  show,
  proceed,
  title,
  content,
  onVerify,
  confirmText,
  requireVerification = true,
}) {
  const { t } = useTranslation(['common', 'placeholder', 'helper_text', 'sorting']);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifiedValue, setVerifiedValue] = useState('');
  const { playSuccess, playFail } = useScanSound();

  const handleChange = useCallback((newValue) => {
    setInputValue(newValue);
    setError('');
    setSuccess(false);
    setVerifiedValue('');
  }, []);

  const handleVerify = useCallback(async () => {
    if (!inputValue.trim()) return;

    setError('');
    setSuccess(false);

    try {
      if (onVerify) {
        const result = await onVerify(inputValue.trim());
        if (result.success) {
          playSuccess();
          setSuccess(true);
          setVerifiedValue(result.value || inputValue.trim());
          setError('');
        } else {
          playFail();
          setSuccess(false);
          setError(result.error || t('common:message.verification_failed'));
        }
      } else {
        playSuccess();
        setSuccess(true);
        setVerifiedValue(inputValue.trim());
      }
    } catch (err) {
      playFail();
      setSuccess(false);
      setError(err.message || t('common:message.verification_failed'));
    }
  }, [inputValue, onVerify, t, playSuccess, playFail]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleVerify();
      }
    },
    [handleVerify],
  );

  return (
    <StackedModal
      isOpen={show}
      title={title || t('sorting:scan_tote')}
      content={
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          rowGap={1}
        >
          <InlineStack
            gap="100"
            blockAlign="center"
          >
            <Typography
              variant="body_md_semibold"
              sx={{ color: '#303030' }}
            >
              {content || t('sorting:scan_tote_description')}
            </Typography>
            <Tooltip content={t('helper_text:tote_scan')}>
              <Box
                sx={{
                  display: 'flex',
                  width: 20,
                  height: 20,
                  '& .Polaris-Icon': { width: 20, height: 20, margin: 0 },
                }}
              >
                <Icon
                  source={InfoIcon}
                  tone="base"
                />
              </Box>
            </Tooltip>
          </InlineStack>
          <InlineStack
            wrap={false}
            blockAlign="start"
            gap="200"
          >
            <div
              style={{ flexGrow: 1 }}
              onKeyDown={handleKeyDown}
            >
              <TextField
                value={inputValue}
                onChange={handleChange}
                prefix={<Icon source={BarcodeIcon} />}
                suffix={
                  success || error ? (
                    <span className={success ? 'icon-success' : 'icon-critical'}>
                      <Icon source={success ? CheckCircleIcon : XCircleIcon} />
                    </span>
                  ) : undefined
                }
                autoComplete="off"
                size="slim"
                focused={true}
                placeholder={t('placeholder:tote')}
                error={error}
              />
            </div>
          </InlineStack>
        </Box>
      }
      primaryAction={{
        content: confirmText || t('common:button.confirm'),
        disabled: requireVerification ? !success : inputValue.trim() && !success,
        onAction: () => {
          proceed({ confirmed: true, value: verifiedValue });
        },
      }}
      onClose={() => {
        proceed({ confirmed: false, value: null });
      }}
    />
  );
}

export default confirmable(ToteScanDialog);
